/**
 * Streamlined App Insights Integration (Phase 6 Optimized)
 *
 * Provides Azure Application Insights integration with:
 * - Maximum App Insights native features enabled
 * - React plugin for enhanced component tracking
 * - Minimal custom code approach
 * - Optimized for production debugging
 */

import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { History } from 'history';

import type { LogLevelConfig } from './logLevelManager';
import { getCurrentLevel, LogLevel, onLevelChange, shouldLog } from './logLevelManager';

export interface AppInsightsConfig {
  connectionString: string;
  history?: History; // React Router history
}

let appInsights: ApplicationInsights;
let reactPlugin: ReactPlugin;

/**
 * Initialize Application Insights with React plugin and dynamic configuration
 */
export function initializeAppInsights(config: AppInsightsConfig): void {
  const environment = import.meta.env.MODE || 'unknown';

  // Initialize React plugin for enhanced component tracking
  reactPlugin = new ReactPlugin();

  appInsights = new ApplicationInsights({
    config: {
      connectionString: config.connectionString,
      // MAXIMUM App Insights native features enabled
      enableAutoRouteTracking: true, // Automatic page tracking
      enableRequestHeaderTracking: true, // Automatic API tracking
      enableResponseHeaderTracking: true, // Enhanced API insights
      enableCorsCorrelation: true, // Automatic correlation
      enableUnhandledPromiseRejectionTracking: true, // Automatic error tracking
      disableFetchTracking: false, // Keep automatic fetch tracking
      disableAjaxTracking: false, // Keep automatic AJAX tracking

      // Dynamic sampling based on environment and log level
      samplingPercentage: getSamplingPercentage(),
      enableDebug: shouldLog(LogLevel.DEBUG),

      // React plugin integration for enhanced component tracking
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: {
          history: config.history, // React Router history for automatic navigation tracking
        },
      },
    },
  });

  appInsights.loadAppInsights();
  appInsights.trackPageView();

  // Listen for log level changes to adjust App Insights sampling
  onLevelChange(config => {
    updateAppInsightsSampling(config);
  });

  console.info('App Insights with React plugin initialized (STREAMLINED)', {
    samplingPercentage: getSamplingPercentage(),
    debugEnabled: shouldLog(LogLevel.DEBUG),
    environment,
    reactPluginEnabled: true,
    customHooksRemoved: true,
    streamlinedApproach: true,
  });
}

/**
 * Get React plugin instance for HOC usage
 */
export function getReactPlugin(): ReactPlugin {
  return reactPlugin;
}

/**
 * Calculate sampling percentage based on environment and log level
 */
export function getSamplingPercentage(): number {
  // Read from environment variables with fallbacks
  const envSampling = import.meta.env.VITE_APP_INSIGHTS_SAMPLING_PERCENTAGE;

  if (envSampling) {
    const percentage = parseInt(envSampling, 10);

    if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
      return percentage;
    }
  }

  // Environment-based defaults with log level adjustments
  const environment = import.meta.env.MODE;

  if (environment === 'production') {
    // Production: Conservative sampling, increase for debug/trace
    return shouldLog(LogLevel.DEBUG) ? 25 : 10;
  } else if (environment === 'staging') {
    // Staging: Moderate sampling, increase for debug/trace
    return shouldLog(LogLevel.DEBUG) ? 75 : 50;
  } else {
    // Development: Full sampling for comprehensive debugging
    return 100;
  }
}

/**
 * Update App Insights sampling when log level changes
 */
export function updateAppInsightsSampling(config: LogLevelConfig): void {
  if (appInsights) {
    const newSampling = getSamplingPercentage();

    appInsights.config.samplingPercentage = newSampling;
    appInsights.config.enableDebug = shouldLog(LogLevel.DEBUG);

    console.info('App Insights sampling updated', {
      logLevel: LogLevel[config.level],
      samplingPercentage: newSampling,
      debugEnabled: appInsights.config.enableDebug,
      source: config.source,
      timestamp: new Date(config.timestamp).toISOString(),
    });
  }
}

/**
 * Simplified track function - the ONLY custom tracking API we use
 * Phase 6 Optimized: Minimal overhead, maximum App Insights utilization
 */
export function track(eventName: string, properties?: Record<string, string>): void {
  if (appInsights) {
    appInsights.trackEvent({ name: eventName, properties });
  }
}

/**
 * Track custom event with measurements
 */
export function trackWithMeasurements(eventName: string, properties?: Record<string, string>, measurements?: Record<string, number>): void {
  if (appInsights) {
    const enhancedProperties = {
      ...properties,
      sessionLogLevel: LogLevel[getCurrentLevel()],
      environment: import.meta.env.MODE || 'unknown',
      timestamp: new Date().toISOString(),
    };

    appInsights.trackEvent({
      name: eventName,
      properties: enhancedProperties,
      measurements,
    });
  }
}

/**
 * Track exception with context
 */
export function trackException(error: Error, properties?: Record<string, string>): void {
  if (appInsights) {
    const enhancedProperties = {
      ...properties,
      sessionLogLevel: LogLevel[getCurrentLevel()],
      environment: import.meta.env.MODE || 'unknown',
      timestamp: new Date().toISOString(),
    };

    appInsights.trackException({
      exception: error,
      properties: enhancedProperties,
    });
  }
}

/**
 * Set authenticated user context with privacy considerations
 */
export function setUserContext(userId: string, accountId?: string): void {
  if (appInsights) {
    const privacySafeUserId = `${userId.substring(0, 8)}...`; // Partial ID for privacy

    appInsights.setAuthenticatedUserContext(userId, accountId);

    // Track context setting with privacy-safe logging
    track('UserContextSet', {
      userId: privacySafeUserId,
      hasAccountId: Boolean(accountId).toString(),
    });
  }
}

/**
 * Clear user context
 */
export function clearUserContext(): void {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
    track('UserContextCleared');
  }
}

/**
 * Track page view with enhanced properties
 */
export function trackPageView(name?: string, url?: string, properties?: Record<string, string>): void {
  if (appInsights) {
    const enhancedProperties = {
      ...properties,
      sessionLogLevel: LogLevel[getCurrentLevel()],
      environment: import.meta.env.MODE || 'unknown',
    };

    appInsights.trackPageView({
      name,
      uri: url,
      properties: enhancedProperties,
    });
  }
}

/**
 * Track dependency call
 */
export function trackDependency(dependencyData: {
  name: string;
  commandName: string;
  startTime: Date;
  duration: number;
  success: boolean;
  properties?: Record<string, string>;
}): void {
  if (appInsights) {
    const enhancedProperties = {
      ...dependencyData.properties,
      sessionLogLevel: LogLevel[getCurrentLevel()],
      environment: import.meta.env.MODE || 'unknown',
    };

    const dependencyId = `${dependencyData.name}-${Date.now()}`;

    appInsights.trackDependencyData({
      id: dependencyId,
      name: dependencyData.name,
      data: dependencyData.commandName,
      startTime: dependencyData.startTime,
      duration: dependencyData.duration,
      success: dependencyData.success,
      responseCode: dependencyData.success ? 200 : 500,
      properties: enhancedProperties,
    });
  }
}

/**
 * Flush any pending telemetry
 */
export function flush(): void {
  if (appInsights) {
    appInsights.flush();
  }
}

/**
 * Get current App Insights instance (for advanced usage)
 */
export function getAppInsights(): ApplicationInsights | undefined {
  return appInsights;
}

/**
 * Check if App Insights is initialized
 */
export function isInitialized(): boolean {
  return !!appInsights;
}
